import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, Reorder } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlants } from '../hooks/usePlants';
import { PlantListCard } from '../components/plants/PlantListCard';
import { EmptyState } from '../components/plants/EmptyState';
import { pluralize, getWaterStatus } from '../utils/formatters';
import { getEffectiveInterval } from '../utils/wateringSchedule';
import type { PlantWithId } from '../types/plant';

const LOADING_MESSAGES = [
  'Checking on your green friends...',
  'Waking up your plants...',
  'Gathering your garden...',
  'Counting leaves...',
  'Unfurling the collection...',
];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function Home() {
  const { userDoc } = useAuth();
  const { plants, loading, error, plantCount, reorderPlants } = usePlants();
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [reordering, setReordering] = useState(false);
  const [reorderList, setReorderList] = useState<PlantWithId[]>([]);
  const [cityNudgeDismissed, setCityNudgeDismissed] = useState(
    () => localStorage.getItem('plantwise:cityNudgeDismissed') === 'true'
  );

  const showCityNudge = !loading && !cityNudgeDismissed && !userDoc?.city && plants.length >= 3;

  const dismissCityNudge = () => {
    setCityNudgeDismissed(true);
    localStorage.setItem('plantwise:cityNudgeDismissed', 'true');
  };

  const greeting = getGreeting();

  // Helper to get effective interval for a plant using user's climate context
  const getPlantInterval = useCallback(
    (plant: PlantWithId) =>
      getEffectiveInterval(plant, userDoc?.climateZone ?? null, userDoc?.hemisphere ?? null),
    [userDoc?.climateZone, userDoc?.hemisphere],
  );

  const plantsNeedingWater = useMemo(() => {
    return plants.filter((plant) => {
      const status = getWaterStatus(plant.lastWatered, getPlantInterval(plant));
      return status === 'overdue' || status === 'due-soon';
    });
  }, [plants, getPlantInterval]);

  // Sort plants by water urgency
  const sortedPlants = useMemo(() => {
    const statusPriority: Record<string, number> = {
      overdue: 0,
      'due-soon': 1,
      'never-watered': 2,
      fresh: 3,
    };
    return [...plants].sort((a, b) => {
      const statusA = getWaterStatus(a.lastWatered, getPlantInterval(a));
      const statusB = getWaterStatus(b.lastWatered, getPlantInterval(b));
      return (statusPriority[statusA] ?? 4) - (statusPriority[statusB] ?? 4);
    });
  }, [plants, getPlantInterval]);

  // Custom-ordered plants (by sortOrder, then createdAt for unordered)
  const customOrderedPlants = useMemo(() => {
    return [...plants].sort((a, b) => {
      const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aTime = a.createdAt?.toDate?.()?.getTime() ?? 0;
      const bTime = b.createdAt?.toDate?.()?.getTime() ?? 0;
      return bTime - aTime;
    });
  }, [plants]);

  // Which list to display — use custom order once any plant has been reordered
  const hasCustomOrder = plants.some((p) => p.sortOrder != null);
  const displayPlants = reordering
    ? customOrderedPlants
    : hasCustomOrder
      ? customOrderedPlants
      : sortedPlants;

  // Sync reorder list when entering reorder mode
  useEffect(() => {
    if (reordering) setReorderList(customOrderedPlants);
  }, [reordering, customOrderedPlants]);

  const handleReorderDone = async () => {
    setReordering(false);
    const orderedIds = reorderList.map((p) => p.id);
    await reorderPlants(orderedIds);
  };

  // Weekly watering workload for bar chart — predictive when intervals known
  const weeklyWorkload = useMemo((): number[] => {
    const workload: [number, number, number, number, number, number, number] = [0, 0, 0, 0, 0, 0, 0];
    const now = new Date();
    const todayDow = (now.getDay() + 6) % 7; // Mon=0

    plants.forEach((plant, idx) => {
      const interval = getPlantInterval(plant);
      const status = getWaterStatus(plant.lastWatered, interval);

      // Predictive: if interval + lastWatered known, compute next watering day
      if (interval && plant.lastWatered) {
        const lastDate = plant.lastWatered.toDate();
        const nextDate = new Date(lastDate.getTime() + interval * 86400000);
        const diffDays = Math.round((nextDate.getTime() - now.getTime()) / 86400000);

        if (diffDays <= 0) {
          // Overdue — place on today
          workload[todayDow as 0 | 1 | 2 | 3 | 4 | 5 | 6] += 1;
        } else if (diffDays <= 6) {
          // Within this week — place on predicted day
          const targetDow = (todayDow + diffDays) % 7;
          workload[targetDow as 0 | 1 | 2 | 3 | 4 | 5 | 6] += 1;
        }
        // Beyond this week — skip
        return;
      }

      // Fallback: legacy naive placement
      const target =
        status === 'overdue' || status === 'never-watered'
          ? todayDow
          : status === 'due-soon'
            ? Math.min(todayDow + 1, 6)
            : ((idx % 5) + 2) % 7;
      workload[target as 0 | 1 | 2 | 3 | 4 | 5 | 6] += 1;
    });
    return workload;
  }, [plants, getPlantInterval]);

  const todayIndex = (new Date().getDay() + 6) % 7;

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [loading]);

  return (
    <main className="flex-1 px-6 pt-6 pb-8">
      {/* Greeting */}
      <div className="mb-8">
        <h2 className="text-4xl italic font-display font-medium mb-2 text-editorial-black">
          {greeting}
        </h2>
        {!loading && (
          <p className="text-xs font-sans uppercase tracking-[0.15em] text-gray-500">
            {plantCount} {pluralize(plantCount, 'plant')}
            {!userDoc?.tier || userDoc.tier !== 'premium' ? ` in collection` : ''}
          </p>
        )}
      </div>

      {/* Water status banner */}
      {!loading && plants.length > 0 && (
        <div className="mb-8 border border-gray-300 p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-status-green" aria-hidden="true">
            water_drop
          </span>
          <span className="font-sans text-sm text-editorial-black italic font-light">
            {plantsNeedingWater.length > 0
              ? `${plantsNeedingWater.length} ${pluralize(plantsNeedingWater.length, 'plant')} ${plantsNeedingWater.length === 1 ? 'needs' : 'need'} water today`
              : 'All plants are happy — no watering needed'}
          </span>
        </div>
      )}

      {/* City nudge banner */}
      {showCityNudge && (
        <div className="mb-8 border border-gray-200 p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-gray-400" style={{ fontSize: 20 }} aria-hidden="true">
            location_on
          </span>
          <span className="flex-1 font-sans text-sm text-editorial-black italic font-light">
            <Link to="/settings" className="underline underline-offset-2 hover:text-primary transition-colors">
              Set your city
            </Link>
            {' '}for smarter watering schedules
          </span>
          <button
            onClick={dismissCityNudge}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss city setup reminder"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">close</span>
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16" role="status" aria-live="polite">
          <span
            className="material-symbols-outlined text-primary animate-pulse mb-4"
            style={{ fontSize: 32 }}
            aria-hidden="true"
          >
            eco
          </span>
          <p className="text-sm font-serif italic text-gray-500">
            {LOADING_MESSAGES[loadingMessageIndex]}
          </p>
          <span className="sr-only">Loading plants...</span>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="rounded-lg bg-danger/5 border border-danger/20 p-4 text-center" role="alert">
          <p className="text-danger font-sans text-sm">Failed to load plants. Please try again.</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && plants.length === 0 && <EmptyState />}

      {/* Plant list */}
      {!loading && !error && plants.length > 0 && (
        <>
          <div className="editorial-divider mb-8" />

          {plants.length > 1 && (
            <div className="flex justify-end mb-4">
              <button
                onClick={reordering ? handleReorderDone : () => setReordering(true)}
                className="text-xs font-sans uppercase tracking-[0.15em] text-gray-500 py-2 px-1"
                aria-label={reordering ? 'Done reordering plants' : 'Reorder plants'}
              >
                {reordering ? 'Done' : 'Reorder'}
              </button>
            </div>
          )}

          {reordering ? (
            <Reorder.Group
              axis="y"
              values={reorderList}
              onReorder={setReorderList}
              className="space-y-8"
            >
              {reorderList.map((plant) => (
                <Reorder.Item key={plant.id} value={plant} className="list-none">
                  <PlantListCard plant={plant} reordering />
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <div className="space-y-8">
              {displayPlants.map((plant, index) => (
                <motion.div
                  key={plant.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04, duration: 0.3 }}
                >
                  <PlantListCard plant={plant} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Weekly bar chart */}
          <div className="editorial-divider my-8" />

          <div className="mb-4">
            <h3 className="text-lg font-display italic mb-6 text-editorial-black">This Week</h3>
            <div className="flex items-end justify-between h-24 px-2">
              {WEEK_DAYS.map((day, index) => {
                const count = weeklyWorkload[index] ?? 0;
                const maxCount = Math.max(...weeklyWorkload, 1);
                const heightPx = Math.max((count / maxCount) * 64, 4);
                const isToday = index === todayIndex;

                return (
                  <div key={day} className="flex flex-col items-center gap-2 group">
                    <motion.div
                      className={`w-px transition-all duration-300 ${
                        isToday ? 'bg-status-green' : 'bg-editorial-black'
                      }`}
                      initial={{ height: 0 }}
                      animate={{ height: heightPx }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                      style={{ position: 'relative' }}
                    >
                      {isToday && (
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-status-green" />
                      )}
                    </motion.div>
                    <span
                      className={`text-[10px] uppercase font-sans tracking-wider ${
                        isToday
                          ? 'text-editorial-black font-bold'
                          : 'text-gray-500'
                      }`}
                    >
                      {day}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

    </main>
  );
}
